/**
 * Internal barrel for the ConfigurableStep helpers.
 *
 * These pieces back the bridge component at
 * `src/components/Step/variants/ConfigurableStep.tsx` and are NOT part
 * of the package's public surface, except the Product event mapping
 * contract which is also re-exported from
 * `@highereducation/dynamic-experience/utils` for authoring.
 * The public package otherwise re-exports `ConfigurableStep` (and its
 * prop type) only.
 */
export { extractDataSources } from './extractDataSources';
export { isResultLike } from './isResultLike';
export { isSubmittableResult } from './isSubmittableResult';
export { extractResultsPageConfig } from './extractResultsPageConfig';
export {
  extractEntryPointConfig,
  applyEntryPointDcsDefaults,
  applyEntryPointDefaultsAtCommit,
  getEntryPointConfigFromSteps,
} from './extractEntryPointConfig';
export { extractTrackingConfig } from './extractTrackingConfig';
export {
  PRODUCT_EVENT_CONFIG_LOCATION,
  PRODUCT_EVENT_LITERAL_WIRE_KEYS,
  PRODUCT_EVENT_NEVER_SLUGIFY_WIRE_KEYS,
  PRODUCT_EVENT_NON_MAPPABLE_WIRE_KEYS,
  PRODUCT_EVENT_RESOLVE_ROOT,
  PRODUCT_EVENT_SLUGIFY_WIRE_KEYS,
  PRODUCT_EVENT_UNSET_DEFAULTS,
  PRODUCT_EVENT_WIRE_KEYS,
  RECOMMENDED_EL_PRODUCT_ID_DATAPATH,
  allowsProductFieldLiteral,
  isProductEventWireKey,
  parseCustomDimensionMapping,
  parseProductFieldMapping,
  shouldSlugifyCustomDimensionValue,
  shouldSlugifyProductField,
  type CustomDimensionMapping,
  type CustomDimensionParseResult,
  type ProductEventTrackingConfig,
  type ProductEventWireKey,
  type ProductFieldMapping,
  type ProductFieldParseResult,
  type ProductFieldsConfig,
} from './productEventMappingContract';
export { contentDeclaresProductSurface } from './contentDeclaresProductSurface';
export { buildEntryFieldSummary } from './buildEntryFieldSummary';
export {
  listProductFieldFallbacks,
  resolveMappingValue,
  resolveProductFields,
  slugifyProductValue,
  type ResolvableProductField,
} from './resolveProductFields';
export {
  applyProductContext,
  mergeCustomDimensions,
  resolveConfiguredCustomDimensions,
  type ApplyProductContextArgs,
} from './applyProductContext';
export {
  buildFlowContext,
  SUBJECT_LABEL_WITH_FALLBACK_KEY,
  type BuildFlowContextOptions,
} from './buildFlowContext';
export { projectRequestContext } from './projectRequestContext';
export { buildLeadContext } from './buildLeadContext';
export { buildTrackingContext } from './buildTrackingContext';
export {
  buildLinkoutTrackingContext,
  DEFAULT_LINKOUT_FORMAT_TYPE,
  type LinkoutTrackingContextInput,
} from './buildLinkoutTrackingContext';
export { deriveInitialFormState } from './deriveInitialFormState';
export {
  mergeLandingHostFormSeed,
  resolveLandingHostFormSeed,
  resolveLandingStepIds,
  sanitizeInitialFormState,
  seedHostOwnedFormState,
  seedInitialFormState,
} from './seedInitialFormState';
export { readStepFlowType } from './readStepFlowType';
export { readStepMonetizationChannel } from './readStepMonetizationChannel';
export { readStepTrafficSource } from './readStepTrafficSource';
export { readInferredCountryCode } from './readInferredCountryCode';
export { resolveGeolocation } from './resolveGeolocation';
export {
  findBoundComponent,
  buildBoundComponentIndex,
  buildBoundFieldIndex,
  FORM_COMPONENT_TYPE,
  type BoundComponentInfo,
  type BoundFieldIndex,
  type BoundFormFields,
} from './findBoundComponent';
export {
  LEAD_SUBMIT_STATE_PREFIX,
  isLeadSubmitStateKey,
  leadSubmitFieldErrorKey,
  leadSubmitStateKey,
} from './leadSubmitState';
export { resolveDisplayLabel } from './resolveDisplayLabel';
export { resolveSourceSlug } from './resolveSourceSlug';
export { buildOptionFromFormState } from './buildOptionFromFormState';
export {
  createActionDispatcher,
  type ActionDispatcherDeps,
  type ActionHandler,
  type ActionDispatchOutcome,
} from './createActionDispatcher';
export { dispatchNamedModalAction } from './dispatchNamedModalAction';
/**
 * @deprecated Re-export of the DB-1.5 factory under its legacy name.
 * Replaced by {@link createActionDispatcher} in DB-2.1 (EDUN-10689).
 * Remove with the DB-2.2 PR.
 */
export {
  createNavigationActionDispatcher,
  type NavigationDispatcherDeps,
} from './createNavigationActionDispatcher';
export {
  translateFormStateChange,
  type TranslateFormStateChangeArgs,
} from './translateFormStateChange';
export {
  resolveDataSourceDeclarations,
  deriveTrustedOriginsFromApiConfig,
  deriveReportingQueryUrl,
  appendReportingQueryOrigin,
  findServiceForPath,
} from './resolveDataSourceDeclarations';
export {
  attachServiceCredentials,
  buildMobiusBodyCoercer,
  buildOfferingsTaxonomyBodyCoercer,
  composeRequestBodyCoercers,
  type ServiceCredentials,
} from './attachServiceCredentials';
export { buildOfferingsPagingBodyCoercer } from './buildOfferingsPagingBodyCoercer';
export { buildOfferingsMatchResponseCoercer } from './buildOfferingsMatchResponseCoercer';
export { buildRankingsResponseCoercer } from './buildRankingsResponseCoercer';
export {
  isRankingsListPath,
  RANKINGS_PATH_PREFIX,
} from './rankingsResultsSourceIdentity';
export {
  composeResponseCoercers,
  type ResponseCoercer,
} from './composeResponseCoercers';
export {
  buildConversionRedirectUrl,
  CONVERSION_INIT_PATH,
  MAX_TRACKING_CONTEXT_JSON_LENGTH,
  type ConversionRedirectParams,
} from './buildConversionRedirectUrl';
export {
  LEAD_FIELD_NAMES,
  LEAD_FIELD_PRESETS,
  isLeadFieldName,
  validateLeadFields,
  type LeadFieldInput,
  type LeadFieldName,
  type LeadFieldPreset,
  type LeadValidationResult,
} from './leadFieldPresets';
